import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { ScanFilterStateService } from "src/app/services/scan-filter-state.service";
import { BreadcrumbService } from "src/app/services/breadcrumb.service";
import { DashboardService } from "src/app/services/home/dashboard.service";
import { reportErrorState } from "src/app/shared/api-error.util";
import { exportScanHistoryExcel } from "src/app/shared/scan-history-export";

type AlertColor = "red" | "orange" | "purple" | "green" | "yellow" | "blue";
type DrawerTab = "overview" | "history" | "consumer";

/** One GetRecentAlerts row, shaped the same way dashboard-new.component.ts
 *  shapes its own Recent Alerts rows so the drawer below shows identical
 *  content. */
interface AlertRow {
  seqNo: string | null;
  status: string;
  remarks: string;
  label: string;
  subtitle: string;
  category: string;
  color: AlertColor;
  sev: string;
  sevLabel: string;
  time: string;
  timeAbs: string;
  alertTime: string | null;
  firstScanDate: string | null;
  firstScanAbs: string;
  totalScanCount: number | null;
  scanStatus: string | null;
  loc: string;
  address: string | null;
  productName: string | null;
  productCode: string | null;
  batchNo: string | null;
  productImage: string | null;
  verificationMode: string | null;
  deviceDetails: string | null;
  ipAdrs: string | null;
  lat: number | null;
  lng: number | null;
  scanHistory: any[];
}

interface ConsumerGroup {
  name: string | null;
  mobile: string | null;
  email: string | null;
  address: string | null;
  count: number;
}

/** Colors that have a rar-tint-* / rar-solid-* class in the stylesheet.
 *  Classes rather than [style.*] bindings: Angular 8's style sanitizer
 *  silently drops any value containing var(--...), which blanked the
 *  drawer's icon background. */
const KNOWN_COLORS = ["blue", "green", "orange", "purple", "red", "yellow"];
const SCAN_HISTORY_STATUS_COLOR: { [k: string]: string } = {
  active: "green",
  fake: "red",
  duplicate: "orange",
  counterfeit: "red",
};

@Component({
  selector: "app-recent-alerts-report",
  templateUrl: "./recent-alerts-report.component.html",
  // Reuses the Analytics report's own stylesheet for the page chrome
  // (topbar, table card, pagination) so it matches the other Scan
  // Intelligence reports; the second file holds the Alert Details drawer.
  styleUrls: [
    "../analytics/analytics.component.css",
    "./recent-alerts-report.component.css",
  ],
})
export class RecentAlertsReportComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private static readonly THEME_EVENT = "sa-theme-change";
  isLightMode = document.body.classList.contains("light-mode");

  rows: AlertRow[] = [];
  loading = false;
  hasLoaded = false;
  loadErrorState: { title: string; message: string } | null = null;

  searchTerm = "";
  private searchDebounceTimer: any = null;

  currentPage = 1;
  pageSize = 5;
  rowsPerPageOptions = [5, 10, 25, 50, 100];
  totalRecords = 0;
  totalPages = 1;

  // ── drawer ──
  selected: AlertRow | null = null;
  selectedIndex: number | null = null;
  drawerTab: DrawerTab = "overview";
  consumers: ConsumerGroup[] = [];
  mapEmbedUrl: SafeResourceUrl | null = null;
  toastMsg = "";
  private toastTimer: any = null;

  private dateRangeSub: Subscription | null = null;

  private themeChangeHandler = (): void => {
    this.isLightMode = document.body.classList.contains("light-mode");
  };

  private syncBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#syncBtn")) {
      e.stopPropagation();
      this.loadAlerts();
    }
  };

  constructor(
    private router: Router,
    private scanFilterState: ScanFilterStateService,
    public breadcrumbService: BreadcrumbService,
    private dashboardService: DashboardService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.items = ["Dashboard", "Recent Alerts"];
    // BehaviorSubject - emits the current range immediately, then again on
    // every navbar date change.
    this.dateRangeSub = this.scanFilterState.dateRange$.subscribe(() => {
      this.currentPage = 1;
      this.loadAlerts();
    });
  }

  ngAfterViewInit(): void {
    document.addEventListener("click", this.syncBtnClickHandler);
    window.addEventListener(
      RecentAlertsReportComponent.THEME_EVENT,
      this.themeChangeHandler,
    );
  }

  ngOnDestroy(): void {
    if (this.dateRangeSub) this.dateRangeSub.unsubscribe();
    clearTimeout(this.searchDebounceTimer);
    clearTimeout(this.toastTimer);
    document.removeEventListener("click", this.syncBtnClickHandler);
    window.removeEventListener(
      RecentAlertsReportComponent.THEME_EVENT,
      this.themeChangeHandler,
    );
    this.breadcrumbService.items = [];
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.closeDrawer();
  }

  goToDashboard(): void {
    this.router.navigate(["/dashboard1"]);
  }

  // ===================== Data =====================
  private getCompId(): number {
    try {
      const raw = localStorage.getItem("user");
      const compId = raw ? JSON.parse(raw).compId : null;
      return compId || 1;
    } catch {
      return 1;
    }
  }

  private buildPayload(pageNo: number, pageSize: number) {
    const range = this.scanFilterState.dateRangeSnapshot;
    return {
      compId: this.getCompId(),
      fromDt: range.dateFrom || "",
      toDt: range.dateTo || "",
      pageNo,
      pageSize,
      search: this.searchTerm.trim(),
    };
  }

  /** The list may come back as details.data, details.recentAlerts or as
   *  details itself - accept all three. */
  private extractList(details: any): any[] {
    if (Array.isArray(details)) return details;
    if (!details) return [];
    if (Array.isArray(details.data)) return details.data;
    if (Array.isArray(details.recentAlerts)) return details.recentAlerts;
    return [];
  }

  private extractTotal(details: any, listLength: number): number {
    const candidates = [
      details && details.totalRows,
      details && details.totalCount,
      details && details.totalRecords,
      details && details.total,
    ];
    for (const c of candidates) {
      if (typeof c === "number") return c;
    }
    // Some list APIs put the total on every row instead.
    const list = this.extractList(details);
    if (list.length && typeof list[0].totalCount === "number") {
      return list[0].totalCount;
    }
    if (list.length && typeof list[0].totalRows === "number") {
      return list[0].totalRows;
    }
    return listLength;
  }

  loadAlerts(): void {
    this.loading = true;
    this.loadErrorState = null;

    this.dashboardService
      .getRecentAlerts(this.buildPayload(this.currentPage, this.pageSize))
      .subscribe({
        next: (res: any) => {
          this.loading = false;
          const ok =
            res && (res.responseCode === "200" || res.responseCode === 200);
          if (!ok && !(res && res.details)) {
            this.loadErrorState = reportErrorState({ error: res });
            return;
          }
          const list = this.extractList(res.details);
          this.rows = list.map((a) => this.mapAlert(a));
          this.totalRecords = this.extractTotal(res.details, this.rows.length);
          this.totalPages = Math.max(
            1,
            res.details && typeof res.details.totalPages === "number"
              ? res.details.totalPages
              : Math.ceil(this.totalRecords / this.pageSize),
          );
          this.hasLoaded = true;
          this.closeDrawer();
        },
        error: (err: any) => {
          this.loading = false;
          console.error("GetRecentAlerts error:", err);
          this.loadErrorState = reportErrorState(err);
        },
      });
  }

  private mapAlert(a: any): AlertRow {
    const meta = this.alertMetaFromRemarks(a.remarks);
    const statusColor = this.colorFromStatus(a.status);
    const lat = parseFloat(a.usrlatitude != null ? a.usrlatitude : a.latitude);
    const lng = parseFloat(
      a.usrlongitude != null ? a.usrlongitude : a.longitude,
    );
    const hasCoords = !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
    return {
      seqNo:
        a.qrData ||
        (a.verificationId != null ? String(a.verificationId) : null),
      status: a.status || meta.label,
      remarks: a.remarks || "",
      label: a.status || meta.label,
      subtitle: a.remarks || meta.subtitle,
      category: a.status || meta.label,
      color: statusColor || meta.color,
      sev: (a.severity || "info").toLowerCase(),
      sevLabel: a.severity ? String(a.severity).toUpperCase() : "N/A",
      time: this.relTime(a.alertTime),
      timeAbs: this.fmtDateTime(a.alertTime),
      alertTime: a.alertTime || null,
      firstScanDate: a.firstScanDate || null,
      firstScanAbs: this.fmtDateTime(a.firstScanDate),
      totalScanCount: a.totalScanCount != null ? a.totalScanCount : null,
      scanStatus: a.scanStatus || null,
      loc: a.location || a.stateName || "",
      address: a.address || null,
      productName: a.productName || null,
      productCode: a.productCode || null,
      batchNo: a.batchNo || null,
      productImage: a.productImage || null,
      verificationMode: a.verificationMode || null,
      deviceDetails: a.deviceDetails || null,
      ipAdrs: a.ipAdrs || null,
      lat: hasCoords ? lat : null,
      lng: hasCoords ? lng : null,
      scanHistory: Array.isArray(a.duplicateScanDetails)
        ? a.duplicateScanDetails
        : [],
    };
  }

  /** Same rules as dashboard-new's alertMetaFromRemarks - there's no
   *  dedicated alert-type field, so type/color come from the remarks text
   *  when the status doesn't say. */
  private alertMetaFromRemarks(remarks: string): {
    label: string;
    subtitle: string;
    color: AlertColor;
  } {
    const r = (remarks || "").toLowerCase().trim();
    if (!r || r.includes("not activated") || r.includes("unverified")) {
      return {
        label: "Unverified Scan",
        subtitle: "QR not activated",
        color: "purple",
      };
    }
    if (
      r.includes("not a valid") ||
      r.includes("counterfeit") ||
      r.includes("fake") ||
      r === "invalid product" ||
      r === "qr code not mapped"
    ) {
      return {
        label: "Fake Product",
        subtitle: "Suspected counterfeit",
        color: "red",
      };
    }
    if (r === "already scanned" || r.includes("multiple")) {
      return {
        label: "Duplicate Scan",
        subtitle: "Code scanned multiple times",
        color: "yellow",
      };
    }
    if (r === "genuine product" || r === "verified") {
      return {
        label: "Genuine Scan",
        subtitle: "Verified authentic product",
        color: "green",
      };
    }
    return { label: remarks, subtitle: "", color: "blue" };
  }

  private colorFromStatus(status: string): AlertColor | null {
    const s = (status || "").toLowerCase();
    if (s.includes("fake") || s.includes("counterfeit")) return "red";
    if (s.includes("duplicate")) return "yellow";
    // "unverified" contains "verified" - check it first.
    if (s.includes("unverified")) return "purple";
    if (s.includes("genuine") || s.includes("verified")) return "green";
    return null;
  }

  private relTime(iso: string): string {
    if (!iso) return "-";
    const t = new Date(iso).getTime();
    if (isNaN(t)) return "-";
    const secs = Math.round((Date.now() - t) / 1000);
    if (secs < 5) return "just now";
    if (secs < 60) return secs + " sec ago";
    const mins = Math.floor(secs / 60);
    if (mins < 60) return mins + " min ago";
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + "h ago";
    return Math.floor(hrs / 24) + "d ago";
  }

  private fmtDateTime(iso: string): string {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "-";
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  // ===================== Table helpers =====================
  /** Table shows "Fake" rather than "Potential Fake" - same as the
   *  dashboard's Alert column. The drawer keeps the full label. */
  shortLabel(label: string): string {
    return (label || "").replace(/^potential\s+/i, "");
  }

  /** Tinted background + colored glyph/text (table icon, category tag,
   *  scan-history badge) - dashboard's invColorBg + accent color. */
  tintClass(color: string): string {
    return "rar-tint-" + (KNOWN_COLORS.indexOf(color) !== -1 ? color : "blue");
  }

  /** Solid circle + white glyph for the drawer's header icon - dashboard's
   *  alertSolidColor. */
  solidClass(color: string): string {
    return "rar-solid-" + (KNOWN_COLORS.indexOf(color) !== -1 ? color : "blue");
  }

  hideBrokenImage(ev: Event): void {
    const img = ev.target as HTMLElement;
    if (img && img.parentElement) img.parentElement.style.display = "none";
  }

  trackByIndex(i: number): number {
    return i;
  }

  // ===================== Search / pagination =====================
  onSearch(term: string): void {
    this.searchTerm = term;
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.loadAlerts();
    }, 400);
  }

  get rangeStart(): number {
    return this.totalRecords === 0
      ? 0
      : (this.currentPage - 1) * this.pageSize + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  get pageNumbers(): (number | "...")[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: (number | "...")[] = [1];
    if (current - 1 > 2) pages.push("...");
    for (
      let p = Math.max(2, current - 1);
      p <= Math.min(total - 1, current + 1);
      p++
    ) {
      pages.push(p);
    }
    if (current + 1 < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  }

  get showTableSkeleton(): boolean {
    return this.loading && !this.hasLoaded;
  }

  goToPage(page: number | "..."): void {
    if (page === "..." || page === this.currentPage) return;
    this.currentPage = page;
    this.loadAlerts();
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadAlerts();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadAlerts();
    }
  }

  onRowsChange(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
    this.loadAlerts();
  }

  // ===================== Export =====================
  /** Every row matching the current date range/search, not just this page.
   *  pageSize -1 is the backend's "no paging" convention (same as the
   *  Analytics export). */
  exportData(): void {
    this.dashboardService.getRecentAlerts(this.buildPayload(1, -1)).subscribe({
      next: (res: any) => {
        const list = this.extractList(res && res.details).map((a) =>
          this.mapAlert(a),
        );
        this.downloadCsv(list.length ? list : this.rows);
      },
      error: () => this.downloadCsv(this.rows),
    });
  }

  private downloadCsv(list: AlertRow[]): void {
    const headers = [
      "Seq No.",
      "Status",
      "Remarks",
      "Product Name",
      "Product Code",
      "Severity",
      "Time",
      "Channel",
    ];
    const lines = list.map((r) => [
      r.seqNo || "",
      r.status,
      r.remarks,
      r.productName || "",
      r.productCode || "",
      r.sevLabel,
      r.timeAbs,
      r.verificationMode || "",
    ]);
    const esc = (v: string) => `"${String(v || "").replace(/"/g, '""')}"`;
    const csv = [headers, ...lines].map((c) => c.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `recent-alerts-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // ===================== Alert Details drawer =====================
  openDrawer(row: AlertRow, index: number): void {
    this.selected = row;
    this.selectedIndex = index;
    this.drawerTab = "overview";
    this.consumers = this.groupConsumers(row.scanHistory);
    // Keyless Google Maps embed (output=embed needs no API key), centred on
    // the scan - same as the dashboard drawer.
    this.mapEmbedUrl =
      row.lat != null && row.lng != null
        ? this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://maps.google.com/maps?q=${row.lat},${row.lng}&z=16&output=embed`,
          )
        : null;
  }

  closeDrawer(): void {
    this.selected = null;
    this.selectedIndex = null;
  }

  setTab(tab: DrawerTab): void {
    this.drawerTab = tab;
  }

  get isFakeScan(): boolean {
    return (
      !!this.selected &&
      (this.selected.scanStatus || "").toLowerCase() === "fake"
    );
  }

  /** "First Scan" - fake scans have no real firstScanDate, so fall back to
   *  the alert's own time (same as the dashboard drawer). */
  get firstScanText(): string {
    const a = this.selected;
    if (!a) return "-";
    if (a.firstScanDate) return a.firstScanAbs;
    return this.isFakeScan ? a.timeAbs : "-";
  }

  get historyCount(): number | null {
    const a = this.selected;
    if (!a) return null;
    return a.scanHistory.length ? a.scanHistory.length : a.totalScanCount;
  }

  get drawerMobile(): string {
    const a = this.selected;
    return a && a.scanHistory[0] && a.scanHistory[0].mobile
      ? a.scanHistory[0].mobile
      : "-";
  }

  get hasCoords(): boolean {
    return (
      !!this.selected && this.selected.lat != null && this.selected.lng != null
    );
  }

  get mapLinkUrl(): string {
    const a = this.selected;
    return a ? `https://www.google.com/maps?q=${a.lat},${a.lng}` : "";
  }

  historyLoc(s: any): string {
    return [s.city, s.state].filter(Boolean).join(", ") || "-";
  }

  historyBadgeColor(status: string): string {
    return (
      SCAN_HISTORY_STATUS_COLOR[String(status || "").toLowerCase()] || "blue"
    );
  }

  fmt(iso: string): string {
    return this.fmtDateTime(iso);
  }

  /** Consumer Details tab - repeat scans by the same person (mobile, then
   *  email, then name) collapse into one card with a scan count. */
  private groupConsumers(list: any[]): ConsumerGroup[] {
    const groups: ConsumerGroup[] = [];
    const byKey: { [k: string]: ConsumerGroup } = {};
    (list || []).forEach((s: any) => {
      const name = s.verifiedBy || s.consumerName || s.name || null;
      const mobile = s.mobileNumber || s.mobNo || s.mobile || null;
      const email = s.email || null;
      const address =
        s.address || [s.city, s.state].filter(Boolean).join(", ") || null;
      if (!name && !mobile && !email) return;
      const key = (mobile || email || name || "").toLowerCase().trim();
      let g = byKey[key];
      if (!g) {
        g = { name, mobile, email, address, count: 0 };
        byKey[key] = g;
        groups.push(g);
      }
      g.count++;
      g.name = g.name || name;
      g.mobile = g.mobile || mobile;
      g.email = g.email || email;
      g.address = g.address || address;
    });
    return groups;
  }

  /** Scan History tab - download every duplicateScanDetails entry as Excel. */
  exportScanHistory(a: any): void {
    const seqNo = a && (a.seqNo || (a.scanHistory[0] && a.scanHistory[0].qr));
    if (!exportScanHistoryExcel(a && a.scanHistory, seqNo)) {
      this.showToast("No scan history to export");
      return;
    }
    this.showToast("Scan history downloaded");
  }

  copySeqNo(): void {
    const code = this.selected && this.selected.seqNo;
    if (!code || !navigator.clipboard || !navigator.clipboard.writeText) return;
    navigator.clipboard
      .writeText(code)
      .then(() => this.showToast("Code copied"))
      .catch(() => {});
  }

  private showToast(msg: string): void {
    this.toastMsg = msg;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => (this.toastMsg = ""), 2600);
  }
}
